import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from '../components/DelegationBadge';
import {NameserverCount} from '../components/NameserverCount';
import {
    providerColor,
    providerLabel,
} from '../providerLabels';
import config from '../config';

// ⚠️ PROVIDER IS A COLUMN, NOT A GROUPING.
//
// A portfolio spread across three providers is ONE list with provenance as an
// attribute — not three tables, not a grouped table, not a provider switcher.
// That is the point of the neutral layer: `provider` is the only field in the
// schema that reveals where a zone came from, and it earns a column rather than
// restructuring the page.
/**
 * FNP-1378 — WHICH CONNECTION THIS ROW CAME FROM.
 *
 * ⚠️ A LOOKUP, NOT A FIELD ON THE ZONE. `DnsZone` carries `connectionId`, an
 * opaque `PROVIDER_CONNECTION#tenant#uuid` that means nothing to a reader. The
 * tenant's own `displayName` is on the connection, and the table already loads
 * the connections for the Add button, so the name is resolved here rather than
 * pushed into the zone shape.
 */
export type ZoneConnectionNames = ReadonlyMap<string, string>;

/**
 * The last segment of a connection id, for the moment before names arrive.
 *
 * ⚠️ NOT THE WHOLE ID. `PROVIDER_CONNECTION#<tenant-uuid>#<connection-uuid>` is
 * 80 characters and its first two thirds are identical on every row, so
 * rendering it whole would push the table wide to show a prefix that
 * distinguishes nothing. The tail is the only part that differs.
 *
 * Shown dimmed, because it is a placeholder for a name rather than a value a
 * tenant should ever need to read.
 */
export const shortConnectionId = (connectionId: string): string => {
    const tail = connectionId.split('#').pop() ?? connectionId;
    return tail.length > 8 ? tail.slice(0, 8) : tail;
};

export const useZonesTableColumns = (
    /**
     * Connection id → the tenant's own name for it. Empty while connections
     * load, and a row whose id is missing falls back to the id's own tail
     * rather than rendering blank — an unnamed row is still more use than no
     * way to tell two rows apart.
     */
    connectionNames: ZoneConnectionNames = new Map(),
) => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<DnsZone>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // FNP-1284 - NO WIDTH ON THE FLEXIBLE COLUMN. Leave it that way.
            //
            // A column with no width takes the space the fixed columns leave. Giving
            // this one a grid fraction (`2fr`) instead made it content-size: the
            // fixed columns are committed first, and the name column then grew to fit
            // its longest value, pushing the table wider than its container and
            // raising a horizontal scrollbar.
            //
            // `minWidth` in px is fine here if a floor is ever wanted. A grid
            // fraction is not: mantine-datatable puts the value in an inline style on
            // a real <table> cell, where `fr` is not a valid unit.
            render: (item: DnsZone) => (
                // ⚠️ The link carries `connectionId`, because `/zones/:zoneId`
                // alone cannot address a zone — getDnsZone needs both. See
                // hooks/useZone for the full resolution order.
                <NavLink
                    to={`${config.basePath}/${encodeURIComponent(item.id)}?connectionId=${encodeURIComponent(item.connectionId)}`}
                >
                    <Text size={'sm'}>{item.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'provider',
            sortable: true,
            title: I18n.get('Provider'),
            render: (item: DnsZone) => (
                <Badge variant={'light'} color={providerColor(item.provider)}>
                    {providerLabel(item.provider)}
                </Badge>
            ),
        },
        // FNP-1378 — WHICH CONNECTION THIS ROW CAME FROM.
        //
        // ⚠️ THE ROWS ARE NOT DUPLICATES, AND THIS COLUMN IS WHAT SAYS SO. The
        // read path fans out across every connection and returns the union,
        // which is correct: connections are normally separate provider
        // accounts, and collapsing by zone NAME would hide a zone that really
        // does exist in two of them — a split-horizon or a genuine
        // misconfiguration, which is exactly what `delegationStatus` exists to
        // surface.
        //
        // But a tenant may also connect the SAME account twice, with different
        // permission levels — read-only for most work, a wider role for
        // occasional changes. `displayName` exists on a connection precisely
        // because "a tenant with a production and a staging Route 53 account
        // has two connections that are otherwise identical". When they do, the
        // same zones come back once per connection and the table showed no
        // difference between the rows: three real zones rendered as nine
        // identical lines, which reads as a rendering bug.
        //
        // Naming the connection makes the list honest without touching the
        // fan-out. Placed beside Provider because they answer the same
        // question at two depths — WHICH provider, then WHICH credential into
        // it.
        {
            accessor: 'connectionId',
            sortable: true,
            title: I18n.get('Connection'),
            render: (item: DnsZone) => {
                const name = connectionNames.get(item.connectionId);
                return (
                    <Text size={'sm'} c={name ? undefined : 'dimmed'}>
                        {name ?? shortConnectionId(item.connectionId)}
                    </Text>
                );
            },
        },
        {
            accessor: 'appearsDelegated',
            sortable: true,
            title: I18n.get('Delegation'),
            render: (item: DnsZone) => <DelegationBadge zone={item}/>,
        },
        {
            accessor: 'isPrivate',
            sortable: true,
            title: I18n.get('Visibility'),
            // `isPrivate` is `Boolean!` — non-null, so unlike the hosted-zones
            // equivalent there is no third state to render here.
            render: (item: DnsZone) => (
                <Badge
                    variant={'light'}
                    color={item.isPrivate ? 'orange' : 'blue'}
                >
                    {item.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
        },
        {
            accessor: 'nameservers',
            sortable: true,
            title: I18n.get('Nameservers'),
            // The COUNT, not the list: four hostnames per row would drown the
            // table. The full delegation set is on the detail page.
            //
            // ⚠️ AND AN EMPTY LIST RENDERS AS UNKNOWN, NOT `0` (FNP-1289) — the
            // list path never reads the delegation set, so zero here is an
            // absence of information, not an answer. See NameserverCount.
            render: (item: DnsZone) => <NameserverCount zone={item}/>,
        },
        // ⚠️ THERE IS DELIBERATELY NO ZONE ID COLUMN.
        //
        // `DnsZone.id` is OPAQUE — Flexi's own identifier, minted by the
        // adapter, never the provider's. Putting it in front of an operator
        // invites them to paste it into the AWS console or a Cloudflare
        // dashboard, where it means nothing. The hosted-zones module shows its
        // zone id precisely because that one IS the Route 53 id.
    ];

    return {
        columns,
    };
};
